import {
  EXTRACT,
  FLOWERS,
  OTHERS,
  normalizeSpecialsDisplayName,
} from '@iheartjane/menu';
import type {
  AppMode,
  ComplianceLanguageSettings,
  Nullish,
  PriceId,
  ProductKind,
  ProductWeight,
  Weight,
} from '@iheartjane/types';
import { PriceIdEnum, ProductWeightsEnum } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';
import includes from 'lodash-es/includes';
import isString from 'lodash-es/isString';

import type {
  AlgoliaPriceId,
  AlgoliaProduct,
  JaneSearchState,
  KindFacet,
} from '@jane/search/types';
import type { StoreWithMenuProduct } from '@jane/shared-ecomm/data-access';
import type { WeightSelectorWeight } from '@jane/shared-ecomm/types';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, Store, StoreSpecial } from '@jane/shared/models';
import type {
  MaterializedMenuProduct,
  Store as ZodStore,
} from '@jane/shared/types';

import type { MenuRowRules } from './menuRows';
import {
  lowestPriceForMenuProduct,
  parsePriceAtPriceId,
} from './pricing/lowestPriceForMenuProduct';
import { getActualUnitPrice, getUnitPrice } from './pricing/pricing';

// mirrors the defaults specified in the MenuProduct model
const DEFAULT_FLOWER_WEIGHT = 'eighth_ounce';
const DEFAULT_EXTRACT_WEIGHT = 'half_gram';

const ALL_VALS_IDS_AND_LABELS: Weight[] = [...FLOWERS, ...EXTRACT, ...OTHERS];

export type ProductKindSoldByWeight = 'extract' | 'flower' | 'vape';

export const WEIGHT_FILTER_VALUES: AlgoliaPriceId[] = [
  'half gram',
  'gram',
  'two gram',
  'eighth ounce',
  'quarter ounce',
  'half ounce',
  'ounce',
];

type StoreWithComplianceLanguage = {
  store_compliance_language_settings?: Nullish<ComplianceLanguageSettings>;
};

export const bundleComplianceWord = (
  store?: Nullish<StoreWithComplianceLanguage>
) =>
  typeof store?.store_compliance_language_settings?.['bundle'] === 'string'
    ? store.store_compliance_language_settings['bundle']
    : 'bundle';

export const bundleSpecialComplianceWord = (
  store?: Nullish<StoreWithComplianceLanguage>
) => `${bundleComplianceWord(store)} ${specialsComplianceWord(store)}`;

const specialsComplianceWord = (store?: Nullish<StoreWithComplianceLanguage>) =>
  store?.store_compliance_language_settings?.['specials'] || 'special';

interface DefaultWeightIdArgs {
  appliedWeightFilter?: PriceId | '';
  applyNswFix?: boolean;
  defaultWeight?: PriceId;
  menuProduct: MenuProduct | AlgoliaProduct;
  menuRowRules?: MenuRowRules;
  showOnlyWeights?: string[] | null;
  sortedByPrice?: boolean;
  special?: StoreSpecial;
}

export function defaultWeightIdForMenuProductFromFilters({
  appliedWeightFilters,
  menuProduct,
}: {
  appliedWeightFilters: string[];
  menuProduct: MenuProduct | AlgoliaProduct;
}) {
  if (!isSoldByWeight(menuProduct.kind)) return 'each';

  const formattedWeightFilters = appliedWeightFilters
    .map((weight) => weight && weight.replace(' ', '_'))
    .filter((weight): weight is PriceId =>
      PriceIdEnum.includes(weight as PriceId)
    );

  return lowestPriceForMenuProduct(
    menuProduct,
    formattedWeightFilters.filter((weight) => menuProduct[`price_${weight}`])
  );
}

export function defaultWeightIdForMenuProduct({
  applyNswFix,
  defaultWeight,
  menuProduct,
  menuRowRules,
  showOnlyWeights,
  special,
  appliedWeightFilter,
  sortedByPrice,
}: DefaultWeightIdArgs) {
  if (!isSoldByWeight(menuProduct.kind)) return 'each';

  const availableWeights: PriceId[] = availableWeightsForMenuProduct(
    menuProduct,
    showOnlyWeights
  );

  // If the PDP has a defaultWeight query param, we prioritize that selection here
  if (defaultWeight && includes(availableWeights, defaultWeight)) {
    return defaultWeight;
  }

  if (appliedWeightFilter && appliedWeightFilter.length > 0)
    return appliedWeightFilter;

  // if sorting by price (low-to-high or high-to-low), choose cheapest option as initialPriceId
  if (sortedByPrice) {
    return lowestPriceForMenuProduct(menuProduct, availableWeights);
  }

  // If in a menu row with price thresholds, choose cheapest qualifying weight
  if (menuRowRules?.product_threshold) {
    const { minimum_price, maximum_price } = menuRowRules.product_threshold;
    const qualifyingWeights = availableWeights.filter((weight) => {
      const price = parsePriceAtPriceId(menuProduct, weight);
      if (price == null) return false;
      if (minimum_price != null && price < minimum_price) return false;
      if (maximum_price != null && price > maximum_price) return false;
      return true;
    });

    if (qualifyingWeights.length > 0) {
      return lowestPriceForMenuProduct(menuProduct, qualifyingWeights);
    }
  }

  const specialWeights = special?.rules?.includes?.[0]?.weights;
  if (specialWeights && specialWeights.length > 0) {
    return smallestDiscountedWeight(
      availableWeights,
      specialWeights,
      applyNswFix
    );
  }

  const defaultWeightForKind = findDefaultWeightForKind(menuProduct.kind);

  if (
    defaultWeightForKind &&
    includes(availableWeights, defaultWeightForKind)
  ) {
    return defaultWeightForKind;
  }

  return availableWeights[0];
}

function smallestDiscountedWeight(
  availableWeights: PriceId[],
  weightConditions: PriceId[],
  applyFix?: boolean
) {
  if (applyFix && weightConditions.includes('each')) {
    return availableWeights[0];
  }
  return availableWeights.find((weight) =>
    weightConditions.includes(weight)
  ) as PriceId;
}

export function availableWeightsForMenuProduct(
  menuProduct: MenuProduct | AlgoliaProduct | MaterializedMenuProduct,
  showOnlyWeights?: string[] | null
) {
  if (menuProduct && isSoldByWeight(menuProduct.kind)) {
    const weightIds = showOnlyWeights?.length
      ? (showOnlyWeights as PriceId[])
      : allWeightIds(menuProduct.kind);

    return weightIds.filter((weightId) => {
      const priceForWeight = getActualUnitPrice(menuProduct, weightId);
      return !!priceForWeight;
    });
  }
  return [];
}

export function prepareCartProduct(
  store: Store,
  menuProduct: MenuProduct,
  price_id: PriceId,
  count: number
) {
  return {
    product: {
      ...menuProduct,
      count,
      price_id,
      recommended: !!menuProduct.recommended,
    },
    store,
  };
}

export function allWeightIds(productKind: ProductKind): PriceId[] {
  return allWeightsByKind(productKind).map((record) => record.id);
}

function labelForFlowerWeightId(id: PriceId) {
  const found = findWeight('flower', id);
  return found ? found.label : undefined;
}

function labelForExtractWeightId(id: PriceId) {
  const found = findWeight('extract', id);
  return found ? found.label : undefined;
}

export function labelForWeightId(id: PriceId, amount?: string | null) {
  return amount
    ? 'Each'
    : labelForFlowerWeightId(id) || labelForExtractWeightId(id);
}

export function labelForVal(val: string) {
  const item = ALL_VALS_IDS_AND_LABELS.find((record) => record.val === val);
  return item && item.label;
}

export function priceIdAndCount(
  productKind: ProductKind,
  selectedQuantity: number,
  selectedWeight: PriceId
) {
  if (productKind === 'extract' || productKind === 'flower') {
    return {
      count: selectedQuantity,
      price_id: selectedWeight,
    };
  }

  return {
    count: selectedQuantity,
    price_id: 'each',
  };
}

function allWeightsByKind(productKind: ProductKind) {
  if (isSoldByWeight(productKind)) {
    return productKind === 'flower' ? FLOWERS : EXTRACT;
  }
  return [];
}

function findWeight(productKind: ProductKindSoldByWeight, id: string) {
  return allWeightsByKind(productKind).find((record) => record.id === id);
}

export function isSoldByWeight(
  productKind: ProductKind
): productKind is ProductKindSoldByWeight {
  return includes(['flower', 'extract', 'vape'], productKind);
}

export function isSoldByItem(productKind: ProductKind) {
  return !isSoldByWeight(productKind);
}

export function findDefaultWeightForKind(productKind: ProductKind) {
  if (isSoldByWeight(productKind)) {
    return productKind === 'flower'
      ? DEFAULT_FLOWER_WEIGHT
      : DEFAULT_EXTRACT_WEIGHT;
  }
  return undefined;
}

export function weightSelectorWeightsForMenuProduct(
  menuProduct: MenuProduct | AlgoliaProduct | MaterializedMenuProduct,
  special?: StoreSpecial
) {
  const weightIds = allWeightIds(menuProduct.kind);

  return weightIds.reduce<WeightSelectorWeight[]>((accum, weightId) => {
    const price = getActualUnitPrice(menuProduct, weightId, special);

    accum.push({
      // `0` is a real price — a special can discount a weight to nothing — so
      // only an absent price disables the option.
      disabled: price === undefined,
      price,
      value: weightId,
    });

    return accum;
  }, []);
}

export function enabledWeightsForMenuProduct(
  menuProduct: MenuProduct | AlgoliaProduct | MaterializedMenuProduct,
  special?: StoreSpecial
): WeightSelectorWeight[] {
  return weightSelectorWeightsForMenuProduct(menuProduct, special).filter(
    (weight) => !weight.disabled
  );
}
const hasPriceForWeight = (
  stores: readonly StoreWithMenuProduct[],
  weightId: PriceId
) => stores.some((store) => store.menu_product[`price_${weightId}`]);

export function weightSelectorWeightsForProductInStores(
  stores: readonly StoreWithMenuProduct[],
  productKind: ProductKindSoldByWeight
) {
  const weightIds = allWeightIds(productKind);
  const weights = weightIds.map((weightId) => ({
    disabled: !hasPriceForWeight(stores, weightId),
    value: weightId,
  }));

  return weights;
}

export function priceDetailsLabel(
  menuProduct: MenuProduct,
  price_id: PriceId,
  count: number
) {
  const unitPrice = getUnitPrice(menuProduct, price_id);
  if (!unitPrice) return undefined;

  const unitLabel = `${formatCurrency(unitPrice)} each`;

  if (menuProduct.kind === 'flower') {
    return labelForWeightId(price_id);
  }

  if (menuProduct.kind === 'extract' || menuProduct.kind === 'vape') {
    const result =
      count > 1 ? `${labelForWeightId(price_id)} / ${unitLabel}` : unitLabel;

    return result;
  }

  return unitLabel;
}

const storesForWeight = (
  stores: readonly StoreWithMenuProduct[],
  weightId: PriceId
) =>
  stores.filter((store) => {
    const price = store.menu_product[`price_${weightId}`];
    return price && Number(price) !== 0;
  });

export type StoresByWeight = { [K in PriceId]: StoreWithMenuProduct[] };
export function categorizeStoresByWeight(
  stores: readonly StoreWithMenuProduct[],
  productKind: ProductKindSoldByWeight
): StoresByWeight {
  const weightIds = allWeightIds(productKind);
  return weightIds.reduce<{ [K in PriceId]: StoreWithMenuProduct[] }>(
    (weights, weightId) => {
      weights[weightId] = storesForWeight(stores, weightId);
      return weights;
    },
    {} as { [K in PriceId]: StoreWithMenuProduct[] }
  );
}

export function defaultWeightIdForProductInStores(
  productKind: ProductKindSoldByWeight,
  storesByWeight: StoresByWeight
): PriceId {
  const defaultWeight = findDefaultWeightForKind(productKind);

  if (defaultWeight && storesByWeight[defaultWeight].length) {
    return defaultWeight;
  }

  const weightIds = allWeightIds(productKind);
  return (
    weightIds.find((weight) => storesByWeight[weight].length > 0) ||
    DEFAULT_FLOWER_WEIGHT
  );
}

export function availableStoresByWeight(
  storesByWeight: StoresByWeight,
  selectedWeight: PriceId
): StoreWithMenuProduct[] {
  return storesByWeight[selectedWeight] || [];
}

export function getAppliedWeightFilter(
  searchState: JaneSearchState<AlgoliaProduct> | undefined
): PriceId | '' {
  if (!searchState) return '';
  if (!searchState.filters) return '';
  if (!searchState.filters['available_weights']) return '';
  const weightFilter = searchState.filters['available_weights'][0];

  if (isString(weightFilter)) {
    const stringifiedWeight = weightFilter.replace(' ', '_');
    if (ProductWeightsEnum.includes(stringifiedWeight as ProductWeight)) {
      return stringifiedWeight as ProductWeight;
    }
  }
  return '';
}

interface CustomLabelArgs<T> {
  appMode: AppMode;
  attribute?: string | null;
  fallback: T;
  isLineageLabel?: boolean;
  store?: Store | ZodStore | null;
}

export const generateCustomLabel = <T>({
  appMode,
  store,
  attribute,
  isLineageLabel,
  fallback,
}: CustomLabelArgs<T>) => {
  if (attribute === 'discountableBundleProducts')
    return `Your ${bundleSpecialComplianceWord(store)} applies to`;

  const isOhioMarketplace = store?.state === 'Ohio' && appMode === 'default';
  if (
    isOhioMarketplace &&
    attribute?.toLowerCase() === 'sale' &&
    store?.custom_product_type_labels
  ) {
    return (
      store.custom_product_type_labels[attribute.toLowerCase()] || fallback
    );
  }

  if (attribute?.toLowerCase() === 'specials') {
    const customName = store?.specials_row_configs?.custom_display_name;
    return normalizeSpecialsDisplayName(customName);
  }

  if (appMode === 'default') return fallback;

  if (!(store && attribute)) return fallback;

  if (isLineageLabel && store.custom_lineage_labels) {
    return store.custom_lineage_labels[attribute.toLowerCase()] || fallback;
  }
  if (store.custom_product_type_labels) {
    return (
      store.custom_product_type_labels[attribute.toLowerCase()] || fallback
    );
  }

  return fallback;
};

export const sortProductTypes = (
  arrayToSort: KindFacet[],
  referenceArray: string[] | null
) =>
  referenceArray && arrayToSort
    ? arrayToSort
        .concat()
        .sort(
          (kindFacetOne, kindFacetTwo) =>
            referenceArray.indexOf(kindFacetOne.name || kindFacetOne.kind) -
            referenceArray.indexOf(kindFacetTwo.name || kindFacetTwo.kind)
        )
    : arrayToSort;
